// 列表多渠道展示：解析 channelPrices，派生价格行与状态行
// 取值分两层，判断口径与默认渠道完全一致：
//   默认渠道（Apple / Google）读外层标量 record.publishStatus / record.prodStatus
//   三方渠道（XS / EN）读 channelPrices.priceInfo.<channel> 内的同名字段
// 两层共用同一套 0/10/20 枚举，不做额外泛化，也不引入任何聚合字段。

export type ChannelStatusType = 'publish' | 'product'
export type ChannelTone = 'success' | 'error' | 'muted'

export interface ChannelPriceRow {
  key: string
  tag: string
  tooltip: string
  currency: string
  price: string
}

export interface ChannelStatusRow {
  key: string
  tag: string
  tooltip: string
  text: string
  tone: ChannelTone
}

interface ChannelMeta {
  key: string
  tag: string
  tooltip: string
  // 三方渠道在按钮 / 弹窗文案里的展示名，默认渠道不需要
  name?: string
}

// 默认渠道（读外层标量）与其支持的三方渠道清单，按 prodChnl 索引
const DEFAULT_CHANNELS: Record<string, ChannelMeta> = {
  apple: { key: 'apple', tag: 'IOS', tooltip: '苹果后台状态' },
  google: { key: 'google', tag: 'GP', tooltip: 'Google 后台状态' },
}

const THIRD_PARTY_CHANNELS: Record<string, ChannelMeta[]> = {
  apple: [
    { key: 'xsolla_pay', tag: 'XS', tooltip: 'Xsolla 后台状态', name: 'Xsolla' },
    { key: 'enjoy_pay', tag: 'EN', tooltip: 'EnjoyPay 后台状态', name: 'Enjoy' },
  ],
  google: [{ key: 'xsolla_pay', tag: 'XS', tooltip: 'Xsolla 后台状态', name: 'Xsolla' }],
}

// 走多渠道展示的渠道；其余渠道沿用单渠道渲染与判断
export const isMultiChannel = (prodChnl?: string): boolean => !!prodChnl && !!DEFAULT_CHANNELS[prodChnl]

export const getDefaultChannel = (prodChnl?: string): ChannelMeta =>
  (prodChnl && DEFAULT_CHANNELS[prodChnl]) || DEFAULT_CHANNELS.apple

// 该渠道下可开启的三方支付渠道清单，供批量开启 / 关闭菜单使用
export const getThirdPartyChannels = (prodChnl?: string): ChannelMeta[] =>
  (prodChnl && THIRD_PARTY_CHANNELS[prodChnl]) || []

const PUBLISH_TEXT: Record<number, { text: string; tone: ChannelTone }> = {
  10: { text: '成功', tone: 'success' },
  20: { text: '失败', tone: 'error' },
}

const PRODUCT_TEXT: Record<number, { text: string; tone: ChannelTone }> = {
  10: { text: '有效', tone: 'success' },
  20: { text: '失效', tone: 'muted' },
}

const parseChannelPrices = (
  raw: goodsSetting.tableDataItem['channelPrices'],
): goodsSetting.ChannelPrices | undefined => {
  if (!raw) return undefined
  if (typeof raw !== 'string') return raw
  try {
    return JSON.parse(raw)
  } catch {
    return undefined
  }
}

// 只展示当前实际开启的三方渠道；关闭后即使历史价格还在也不展示
const activeThirdPartyChannels = (record: goodsSetting.tableDataItem, prodChnl?: string) => {
  const priceInfo = parseChannelPrices(record.channelPrices)?.priceInfo
  return getThirdPartyChannels(prodChnl)
    .map((channel) => ({ ...channel, info: priceInfo?.[channel.key] }))
    .filter((channel) => channel.info?.enableThirdPay === true)
}

const resolveStatus = (status: unknown, type: ChannelStatusType): { text: string; tone: ChannelTone } => {
  const map = type === 'publish' ? PUBLISH_TEXT : PRODUCT_TEXT
  return map[status as number] || { text: '--', tone: 'muted' }
}

// 三方渠道价格行；默认渠道的官方价格行由 TableContent 保留原有渲染
export const getThirdPartyPriceRows = (record: goodsSetting.tableDataItem, prodChnl?: string): ChannelPriceRow[] =>
  activeThirdPartyChannels(record, prodChnl).map((channel) => ({
    key: channel.key,
    tag: channel.tag,
    tooltip: channel.tooltip,
    currency: channel.info?.prodPriceCurrency || 'USD',
    price: channel.info?.prodPrice ? Number(channel.info.prodPrice).toFixed(2) : '--',
  }))

// 各渠道上同名字段的原始值（默认渠道在前，其后是当前已开启的三方渠道），供按值判断的业务逻辑使用。
// 三方渠道未下发该字段时为 undefined，调用方自行决定怎么算。
export const getChannelFieldValues = (
  record: goodsSetting.tableDataItem,
  field: 'prodStatus' | 'publishStatus' | 'modStatus',
  prodChnl?: string,
): unknown[] => [
  (record as Record<string, any>)[field],
  ...activeThirdPartyChannels(record, prodChnl).map((channel) => channel.info?.[field]),
]

export const getChannelStatusRows = (
  record: goodsSetting.tableDataItem,
  type: ChannelStatusType,
  prodChnl?: string,
): ChannelStatusRow[] => {
  const outerStatus = type === 'publish' ? record.publishStatus : record.prodStatus
  const rows: ChannelStatusRow[] = [{ ...getDefaultChannel(prodChnl), ...resolveStatus(outerStatus, type) }]
  activeThirdPartyChannels(record, prodChnl).forEach((channel) => {
    const status = type === 'publish' ? channel.info?.publishStatus : channel.info?.prodStatus
    rows.push({
      key: channel.key,
      tag: channel.tag,
      tooltip: channel.tooltip,
      ...resolveStatus(status, type),
    })
  })
  return rows
}
